import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import AppButton from "./AppButton";
import "./Paginator.scss";

/**
 * Build a compact list of page numbers with ellipses for a paginator.
 * Example: getPaginationNumbers(5, 10) -> [1, "...", 4, 5, 6, "...", 10]
 */
export function getPaginationNumbers(current, total) {
  if (!total || total < 1) return [];
  const pages = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push("...");
  if (total > 1) pages.push(total);
  return pages;
}

/**
 * Reusable themed paginator built on AppButton — supports light + dark theme.
 *
 * Props:
 *   - page (number, 1-based)
 *   - totalPages (number)
 *   - total (number, optional — total record count for the info string)
 *   - pageSize (number, optional — needed to compute startIdx/endIdx)
 *   - onChange (function: (newPage) => void)
 *   - infoLabel (string, optional — defaults to "records")
 *   - showSinglePage (boolean, optional — render even when totalPages === 1)
 *   - pageSizeOptions (number[], optional — renders a rows-per-page select)
 *   - onPageSizeChange (function: (newSize) => void, required by the above)
 *
 * ⚠️ The rows-per-page select appears ONLY when both `pageSizeOptions` and
 * `onPageSizeChange` are passed. A select that cannot report its change is a
 * control that silently does nothing, which is the failure this component has
 * already been bitten by (three call sites passed `onPageChange`, a prop that
 * does not exist, and their paging was inert).
 */
const Paginator = ({
  page,
  totalPages,
  total,
  pageSize,
  onChange,
  infoLabel = "records",
  showSinglePage = false,
  pageSizeOptions,
  onPageSizeChange,
  className = "",
}) => {
  if (!totalPages || (totalPages <= 1 && !showSinglePage)) return null;
  if (typeof onChange !== "function") return null;

  const showPageSize =
    Array.isArray(pageSizeOptions) &&
    pageSizeOptions.length > 0 &&
    typeof onPageSizeChange === "function";

  const startIdx = total != null && pageSize ? (page - 1) * pageSize + 1 : null;
  const endIdx = total != null && pageSize ? Math.min(page * pageSize, total) : null;
  const numbers = getPaginationNumbers(page, totalPages);

  const go = (n) => {
    if (n < 1 || n > totalPages || n === page) return;
    onChange(n);
  };

  const showNav = totalPages > 1;

  return (
    <div className={`app-paginator ${className}`.trim()}>
      <span className="app-paginator__info">
        {total != null
          ? total === 0
            ? `No ${infoLabel}`
            : `Showing ${startIdx}–${endIdx} of ${total}`
          : `Page ${page} of ${totalPages}`}
      </span>

      {showPageSize && (
        /* ⚠️ An SCSS class, not utilities. This component imports its own
           stylesheet, and a file may not do both — see `.app-paginator__pagesize`. */
        <label className="app-paginator__pagesize">
          Rows
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
      )}

      {showNav && (
        <div className="app-paginator__nav">
          <AppButton
            variant="outline"
            size="icon"
            className="app-paginator__btn"
            onClick={() => go(1)}
            disabled={page === 1}
            aria-label="First page"
          >
            <ChevronsLeft size={16} />
          </AppButton>
          <AppButton
            variant="outline"
            size="icon"
            className="app-paginator__btn"
            onClick={() => go(page - 1)}
            disabled={page === 1}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </AppButton>

          {numbers.map((num, idx) =>
            num === "..." ? (
              <span key={`e-${idx}`} className="app-paginator__ellipsis">…</span>
            ) : (
              <AppButton
                key={`p-${num}`}
                variant={num === page ? "default" : "outline"}
                size="icon"
                className="app-paginator__btn"
                onClick={() => go(num)}
                aria-label={`Page ${num}`}
                aria-current={num === page ? "page" : undefined}
              >
                {num}
              </AppButton>
            )
          )}

          <AppButton
            variant="outline"
            size="icon"
            className="app-paginator__btn"
            onClick={() => go(page + 1)}
            disabled={page >= totalPages}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </AppButton>
          <AppButton
            variant="outline"
            size="icon"
            className="app-paginator__btn"
            onClick={() => go(totalPages)}
            disabled={page >= totalPages}
            aria-label="Last page"
          >
            <ChevronsRight size={16} />
          </AppButton>
        </div>
      )}
    </div>
  );
};

export default Paginator;
